import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  subscribeTripData,
  addTripTransaction,
  updateTripTransaction,
  deleteTripTransaction,
  type TripData,
  type TripTransaction,
} from '@/lib/firestore-trip';
import { useAuth } from '@/contexts/AuthContext';
import { EXPENSE_CATEGORIES, CURRENCY_SYMBOLS } from '@/types';

// ── Category config ────────────────────────────────────────────────────────
const CATEGORY_META: Record<
  string,
  { color: string; gradient: string; emoji: string }
> = {
  exp_7: {
    color: '#7C3AED',
    gradient: 'from-violet-500 to-purple-600',
    emoji: '🚌',
  },
  exp_8: {
    color: '#D97706',
    gradient: 'from-amber-500 to-orange-500',
    emoji: '🍽️',
  },
  exp_9: {
    color: '#059669',
    gradient: 'from-emerald-500 to-teal-600',
    emoji: '🏠',
  },
  exp_1: {
    color: '#DC2626',
    gradient: 'from-red-500 to-rose-600',
    emoji: '⚡',
  },
  exp_2: {
    color: '#2563EB',
    gradient: 'from-blue-500 to-indigo-600',
    emoji: '🌐',
  },
  exp_3: {
    color: '#16A34A',
    gradient: 'from-green-500 to-emerald-600',
    emoji: '📱',
  },
  exp_4: {
    color: '#9333EA',
    gradient: 'from-purple-500 to-fuchsia-600',
    emoji: '🛡️',
  },
  exp_5: {
    color: '#EA580C',
    gradient: 'from-orange-500 to-red-500',
    emoji: '💳',
  },
  exp_6: {
    color: '#0891B2',
    gradient: 'from-cyan-500 to-blue-500',
    emoji: '📦',
  },
  exp_10: {
    color: '#6B7280',
    gradient: 'from-slate-400 to-gray-500',
    emoji: '💼',
  },
};

function getMeta(id: string) {
  return (
    CATEGORY_META[id] ?? {
      color: '#6B7280',
      gradient: 'from-slate-400 to-gray-500',
      emoji: '💼',
    }
  );
}

function fmt(amount: number, currency: string) {
  const sym = CURRENCY_SYMBOLS[currency] ?? currency;
  return `${sym}${amount.toLocaleString('en-IN')}`;
}

function formatDate(d: Date) {
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
}

function formatTime(d: Date) {
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

function getCatName(id: string) {
  return EXPENSE_CATEGORIES[id]?.en ?? id;
}

// ── Mini donut chart ──────────────────────────────────────────────────────
function DonutChart({
  segments,
  size = 80,
}: {
  segments: { pct: number; color: string }[];
  size?: number;
}) {
  const r = 30;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;

  // Precompute cumulative offsets immutably to avoid mutating a variable during render
  const segmentOffsets = segments.map((_, i) =>
    segments.slice(0, i).reduce((sum, s) => sum + (s.pct / 100) * circ, 0)
  );

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ transform: 'rotate(-90deg)' }}
    >
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="#e2e8f0"
        strokeWidth={10}
      />
      {segments.map((seg, i) => {
        const dash = (seg.pct / 100) * circ;
        const gap = circ - dash;
        return (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={seg.color}
            strokeWidth={10}
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-segmentOffsets[i]}
            strokeLinecap="round"
          />
        );
      })}
    </svg>
  );
}

// ── Progress ring ────────────────────────────────────────────────────────
function ProgressRing({ pct, danger }: { pct: number; danger: boolean }) {
  const r = 38;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const color = danger ? '#EF4444' : pct > 60 ? '#F59E0B' : '#10B981';

  return (
    <svg width={100} height={100} viewBox="0 0 100 100">
      <circle
        cx={50}
        cy={50}
        r={r}
        fill="none"
        stroke="#e2e8f0"
        strokeWidth={10}
      />
      <circle
        cx={50}
        cy={50}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={10}
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeDashoffset={circ / 4}
        strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 1s ease, stroke 0.5s' }}
      />
      <text
        x={50}
        y={50}
        textAnchor="middle"
        dominantBaseline="middle"
        style={{
          fontSize: 18,
          fontWeight: 700,
          fill: color,
          fontFamily: 'inherit',
        }}
      >
        {pct}%
      </text>
      <text
        x={50}
        y={65}
        textAnchor="middle"
        dominantBaseline="middle"
        style={{
          fontSize: 9,
          fill: '#94a3b8',
          fontFamily: 'inherit',
          letterSpacing: '0.05em',
        }}
      >
        USED
      </text>
    </svg>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export function TripDashboard() {
  const { tripId } = useParams<{ tripId: string }>();
  const { user } = useAuth();
  const [trip, setTrip] = useState<TripData | null>(null);
  const [transactions, setTransactions] = useState<TripTransaction[]>([]);
  const [error, setError] = useState(!tripId ? 'Invalid trip link.' : '');
  const [loading, setLoading] = useState(!!tripId);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions'>(
    'overview'
  );
  const [newTxPulse, setNewTxPulse] = useState(false);
  const prevCountRef = useRef(0);

  // ── Admin: add expense panel ────────────────────────────────────────────
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addSuccess, setAddSuccess] = useState(false);
  const [newTxName, setNewTxName] = useState('');
  const [newTxAmount, setNewTxAmount] = useState('');
  const [newTxCat, setNewTxCat] = useState('exp_8');
  const [newTxNote, setNewTxNote] = useState('');

  // ── Admin: edit transaction modal ───────────────────────────────────────
  const [editTx, setEditTx] = useState<TripTransaction | null>(null);
  const [editName, setEditName] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editCat, setEditCat] = useState('');
  const [editNote, setEditNote] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // ── Admin: delete confirm ───────────────────────────────────────────────
  const [deleteTxId, setDeleteTxId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (!tripId) return;
    const unsub = subscribeTripData(
      tripId,
      (t, txs) => {
        setTrip(t);
        setTransactions(txs);
        setLoading(false);
        if (prevCountRef.current > 0 && txs.length > prevCountRef.current) {
          setNewTxPulse(true);
          setTimeout(() => setNewTxPulse(false), 1500);
        }
        prevCountRef.current = txs.length;
      },
      (e) => {
        setError(e.message);
        setLoading(false);
      }
    );
    return unsub;
  }, [tripId]);

  function handleCopyLink() {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  // ── Admin helpers ──────────────────────────────────────────────────────
  const isAdmin = !!(user && trip && user.uid === trip.adminUid);

  async function handleAddExpense() {
    if (!tripId || !newTxName || !newTxAmount) return;
    setAddLoading(true);
    try {
      await addTripTransaction(tripId, {
        name: newTxName, amount: parseFloat(newTxAmount),
        categoryId: newTxCat, notes: newTxNote,
      });
      setNewTxName(''); setNewTxAmount(''); setNewTxNote('');
      setAddSuccess(true);
      setTimeout(() => setAddSuccess(false), 2000);
    } catch (e) { console.error(e); }
    setAddLoading(false);
  }

  function openEditModal(tx: TripTransaction) {
    setEditTx(tx);
    setEditName(tx.name);
    setEditAmount(String(tx.amount));
    setEditCat(tx.categoryId);
    setEditNote(tx.notes ?? '');
  }

  async function handleSaveEdit() {
    if (!tripId || !editTx || !editName || !editAmount) return;
    setEditLoading(true);
    try {
      await updateTripTransaction(tripId, editTx.id, {
        name: editName,
        amount: parseFloat(editAmount),
        categoryId: editCat,
        notes: editNote,
      });
      setEditTx(null);
    } catch (e) { console.error(e); }
    setEditLoading(false);
  }

  async function handleDelete() {
    if (!tripId || !deleteTxId) return;
    setDeleteLoading(true);
    try {
      await deleteTripTransaction(tripId, deleteTxId);
      setDeleteTxId(null);
    } catch (e) { console.error(e); }
    setDeleteLoading(false);
  }

  // ── Loading state ──────────────────────────────────────────────────────
  if (loading)
    return (
      <div style={S.page}>
        <div style={S.center}>
          <div style={S.loadRing} />
          <p
            style={{
              color: '#94a3b8',
              marginTop: 16,
              fontSize: 14,
              letterSpacing: '0.05em',
            }}
          >
            Loading trip...
          </p>
        </div>
      </div>
    );

  // ── Error state ────────────────────────────────────────────────────────
  if (error || !trip)
    return (
      <div style={S.page}>
        <div style={S.center}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🗺️</div>
          <p style={{ color: '#EF4444', fontSize: 15, fontWeight: 600 }}>
            Trip not found
          </p>
          <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 6 }}>
            This link may be invalid or expired.
          </p>
        </div>
      </div>
    );

  // ── Derived data ───────────────────────────────────────────────────────
  const spent = transactions.reduce((s, t) => s + t.amount, 0);
  const remaining = trip.totalFund - spent;
  const pct =
    trip.totalFund > 0
      ? Math.min(100, Math.round((spent / trip.totalFund) * 100))
      : 0;
  const danger = pct > 80;

  const byCategory: Record<string, number> = {};
  transactions.forEach((t) => {
    byCategory[t.categoryId] = (byCategory[t.categoryId] ?? 0) + t.amount;
  });
  const topCats = Object.entries(byCategory).sort((a, b) => b[1] - a[1]);

  const donutSegments = topCats.map(([id, amt]) => ({
    pct: spent > 0 ? Math.round((amt / spent) * 100) : 0,
    color: getMeta(id).color,
  }));

  const avgPerDay = (() => {
    try {
      const start = new Date(trip.startDate);
      const end = new Date(trip.endDate);
      const days = Math.max(
        1,
        Math.round((end.getTime() - start.getTime()) / 86400000) + 1
      );
      return spent / days;
    } catch {
      return 0;
    }
  })();

  const daysLeft = (() => {
    try {
      const end = new Date(trip.endDate);
      const today = new Date();
      const diff = Math.ceil((end.getTime() - today.getTime()) / 86400000);
      return diff;
    } catch {
      return null;
    }
  })();

  return (
    <div style={S.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:.6; transform:scale(.95); } }
        @keyframes livePulse { 0%,100% { box-shadow: 0 0 0 0 rgba(16,185,129,.4); } 50% { box-shadow: 0 0 0 6px rgba(16,185,129,0); } }
        @keyframes slideIn { from { opacity:0; transform:translateX(-8px); } to { opacity:1; transform:translateX(0); } }
        @keyframes newTx { 0%,100% { background: rgba(59,130,246,0); } 50% { background: rgba(59,130,246,0.08); } }
        .tx-row { animation: slideIn .3s ease forwards; }
        .tab-btn { transition: all .2s; }
        .copy-btn:hover { transform: scale(1.05); }
      `}</style>

      <div style={S.container}>
        {/* ── Hero gradient header ───────────────────────────── */}
        <div style={S.heroCard}>
          {/* Background mesh */}
          <div style={S.heroBg1} />
          <div style={S.heroBg2} />

          {/* App brand */}
          <div style={S.brandRow}>
            <div style={S.brandIcon}>💰</div>
            <span style={S.brandName}>MyBalance</span>
            <div style={S.liveBadge}>
              <span style={S.liveDot} />
              Live
            </div>
          </div>

          {/* Trip info */}
          <div style={{ position: 'relative', marginTop: 8 }}>
            <h1 style={S.heroTitle}>{trip.name}</h1>
            <p style={S.heroSub}>
              📅 {trip.startDate} → {trip.endDate}
              {daysLeft !== null && (
                <span style={S.daysTag}>
                  {daysLeft > 0
                    ? `${daysLeft}d left`
                    : daysLeft === 0
                      ? 'Last day!'
                      : 'Trip ended'}
                </span>
              )}
            </p>
          </div>

          {/* Quick stats row */}
          <div style={S.heroStats}>
            <div style={S.heroStat}>
              <div style={S.heroStatLabel}>Budget</div>
              <div style={S.heroStatVal}>
                {fmt(trip.totalFund, trip.currency)}
              </div>
            </div>
            <div style={S.heroStatDivider} />
            <div style={S.heroStat}>
              <div style={S.heroStatLabel}>Spent</div>
              <div
                style={{ ...S.heroStatVal, color: danger ? '#FCA5A5' : '#fff' }}
              >
                {fmt(spent, trip.currency)}
              </div>
            </div>
            <div style={S.heroStatDivider} />
            <div style={S.heroStat}>
              <div style={S.heroStatLabel}>Left</div>
              <div
                style={{
                  ...S.heroStatVal,
                  color: remaining < 0 ? '#FCA5A5' : '#6EE7B7',
                }}
              >
                {remaining < 0 ? '-' : ''}
                {fmt(Math.abs(remaining), trip.currency)}
              </div>
            </div>
          </div>

          {/* Budget bar */}
          <div style={S.heroBudgetBar}>
            <div
              style={{
                ...S.heroBudgetFill,
                width: `${pct}%`,
                background: danger ? '#EF4444' : '#10B981',
              }}
            />
          </div>
          <div style={S.heroBudgetLabels}>
            <span style={{ color: 'rgba(255,255,255,.6)', fontSize: 11 }}>
              0%
            </span>
            <span
              style={{
                color: danger ? '#FCA5A5' : 'rgba(255,255,255,.8)',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {pct}% used
            </span>
            <span style={{ color: 'rgba(255,255,255,.6)', fontSize: 11 }}>
              100%
            </span>
          </div>
        </div>

        {/* ── Insight cards row ──────────────────────────────── */}
        <div style={S.insightRow}>
          <div style={S.insightCard}>
            <div style={S.insightIcon}>📊</div>
            <div style={S.insightVal}>
              {fmt(Math.round(avgPerDay), trip.currency)}
            </div>
            <div style={S.insightLabel}>Avg / day</div>
          </div>
          <div style={S.insightCard}>
            <div style={S.insightIcon}>🧾</div>
            <div
              style={{
                ...S.insightVal,
                animation: newTxPulse ? 'pulse .6s ease 2' : 'none',
              }}
            >
              {transactions.length}
            </div>
            <div style={S.insightLabel}>Expenses</div>
          </div>
          <div style={S.insightCard}>
            <div style={S.insightIcon}>{remaining < 0 ? '🚨' : '✅'}</div>
            <div
              style={{
                ...S.insightVal,
                color: remaining < 0 ? '#EF4444' : '#10B981',
              }}
            >
              {remaining < 0 ? 'Over!' : 'On track'}
            </div>
            <div style={S.insightLabel}>Status</div>
          </div>
        </div>

        {/* ── Tab bar ───────────────────────────────────────── */}
        <div style={S.tabBar}>
          {(['overview', 'transactions'] as const).map((tab) => (
            <button
              key={tab}
              className="tab-btn"
              onClick={() => setActiveTab(tab)}
              style={{
                ...S.tabBtn,
                ...(activeTab === tab ? S.tabBtnActive : {}),
              }}
            >
              {tab === 'overview'
                ? '📈 Overview'
                : `💳 Expenses (${transactions.length})`}
            </button>
          ))}
        </div>

        {/* ── Overview tab ──────────────────────────────────── */}
        {activeTab === 'overview' && (
          <div style={{ animation: 'fadeIn .3s ease' }}>
            {/* Budget ring + top categories */}
            {topCats.length > 0 ? (
              <div style={S.card}>
                <p style={S.sectionTitle}>Spending breakdown</p>
                <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <DonutChart segments={donutSegments} size={90} />
                  </div>
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    {topCats.slice(0, 4).map(([id, amt]) => {
                      const meta = getMeta(id);
                      const pctCat =
                        spent > 0 ? Math.round((amt / spent) * 100) : 0;
                      return (
                        <div key={id}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: 3,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 12,
                                color: '#475569',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 5,
                              }}
                            >
                              <span style={{ fontSize: 13 }}>{meta.emoji}</span>
                              {getCatName(id)}
                            </span>
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#0f172a',
                              }}
                            >
                              {fmt(amt, trip.currency)}
                            </span>
                          </div>
                          <div
                            style={{
                              background: '#f1f5f9',
                              borderRadius: 99,
                              height: 5,
                            }}
                          >
                            <div
                              style={{
                                height: 5,
                                borderRadius: 99,
                                width: `${pctCat}%`,
                                background: meta.color,
                                transition:
                                  'width 0.8s cubic-bezier(0.34,1.56,0.64,1)',
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div style={S.emptyCard}>
                <div style={{ fontSize: 40 }}>✈️</div>
                <p style={{ color: '#94a3b8', fontSize: 14, marginTop: 8 }}>
                  No expenses yet — trip is fresh!
                </p>
              </div>
            )}

            {/* Category chips */}
            {topCats.length > 0 && (
              <div style={S.catChips}>
                {topCats.map(([id, amt]) => {
                  const meta = getMeta(id);
                  return (
                    <div key={id} style={S.catChip}>
                      <span style={{ fontSize: 15 }}>{meta.emoji}</span>
                      <div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                          {getCatName(id)}
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#0f172a',
                          }}
                        >
                          {fmt(amt, trip.currency)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Budget ring card */}
            <div
              style={{
                ...S.card,
                display: 'flex',
                alignItems: 'center',
                gap: 20,
              }}
            >
              <ProgressRing pct={pct} danger={danger} />
              <div>
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#0f172a',
                    margin: 0,
                  }}
                >
                  {danger ? '⚠️ Budget running low' : '✅ Budget on track'}
                </p>
                <p
                  style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}
                >
                  {fmt(spent, trip.currency)} spent of{' '}
                  {fmt(trip.totalFund, trip.currency)}
                </p>
                {remaining > 0 && (
                  <p
                    style={{
                      fontSize: 12,
                      color: '#10B981',
                      margin: '4px 0 0',
                      fontWeight: 500,
                    }}
                  >
                    {fmt(remaining, trip.currency)} remaining
                  </p>
                )}
                {remaining < 0 && (
                  <p
                    style={{
                      fontSize: 12,
                      color: '#EF4444',
                      margin: '4px 0 0',
                      fontWeight: 500,
                    }}
                  >
                    {fmt(Math.abs(remaining), trip.currency)} over budget!
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Transactions tab ───────────────────────────────── */}
        {activeTab === 'transactions' && (
          <div style={{ animation: 'fadeIn .3s ease' }}>
            {transactions.length === 0 ? (
              <div style={S.emptyCard}>
                <div style={{ fontSize: 40 }}>🧾</div>
                <p style={{ color: '#94a3b8', fontSize: 14, marginTop: 8 }}>
                  No expenses added yet.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {transactions.map((tx, i) => {
                  const meta = getMeta(tx.categoryId);
                  return (
                    <div
                      key={tx.id}
                      className="tx-row"
                      style={{
                        ...S.txCard,
                        animationDelay: `${i * 30}ms`,
                      }}
                    >
                      {/* Category icon */}
                      <div
                        style={{
                          ...S.txIcon,
                          background: `linear-gradient(135deg, ${meta.color}22, ${meta.color}44)`,
                          border: `1.5px solid ${meta.color}33`,
                        }}
                      >
                        <span style={{ fontSize: 18 }}>{meta.emoji}</span>
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={S.txName}>{tx.name}</div>
                        <div style={S.txMeta}>
                          <span
                            style={{
                              ...S.txCatTag,
                              background: `${meta.color}18`,
                              color: meta.color,
                            }}
                          >
                            {getCatName(tx.categoryId)}
                          </span>
                          <span style={{ color: '#94a3b8' }}>·</span>
                          <span>{formatDate(tx.createdAt)}</span>
                          <span style={{ color: '#94a3b8' }}>·</span>
                          <span>{formatTime(tx.createdAt)}</span>
                        </div>
                        {tx.notes && <div style={S.txNote}>📝 {tx.notes}</div>}
                      </div>

                      {/* Amount */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                        <div style={S.txAmt}>
                          -{fmt(tx.amount, trip.currency)}
                        </div>
                        {isAdmin && (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              onClick={() => openEditModal(tx)}
                              style={adminMiniBtn}
                              title="Edit"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => setDeleteTxId(tx.id)}
                              style={{ ...adminMiniBtn, background: '#FEF2F2', color: '#EF4444' }}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Edit transaction modal ─────────────────────────── */}
        {editTx && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 380, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 }}>Edit Expense</h3>
                <button onClick={() => setEditTx(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>×</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <input style={adminInput} placeholder="Expense name" value={editName} onChange={e => setEditName(e.target.value)} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <input style={adminInput} type="number" placeholder="Amount" value={editAmount} onChange={e => setEditAmount(e.target.value)} />
                  <select style={adminInput} value={editCat} onChange={e => setEditCat(e.target.value)}>
                    {Object.entries(EXPENSE_CATEGORIES).map(([id, names]) => (
                      <option key={id} value={id}>{names.en}</option>
                    ))}
                  </select>
                </div>
                <input style={adminInput} placeholder="Note (optional)" value={editNote} onChange={e => setEditNote(e.target.value)} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <button
                    onClick={() => setEditTx(null)}
                    style={{ padding: '11px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={editLoading}
                    style={{ padding: '11px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#3B82F6,#7C3AED)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: editLoading ? 'not-allowed' : 'pointer', opacity: editLoading ? 0.7 : 1, fontFamily: 'inherit' }}
                  >
                    {editLoading ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Delete confirm modal ───────────────────────────── */}
        {deleteTxId && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 340, fontFamily: "'Plus Jakarta Sans', sans-serif", textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🗑️</div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>Delete expense?</h3>
              <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 20px' }}>This action cannot be undone.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <button
                  onClick={() => setDeleteTxId(null)}
                  style={{ padding: '11px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteLoading}
                  style={{ padding: '11px', borderRadius: 10, border: 'none', background: '#EF4444', color: '#fff', fontSize: 14, fontWeight: 600, cursor: deleteLoading ? 'not-allowed' : 'pointer', opacity: deleteLoading ? 0.7 : 1, fontFamily: 'inherit' }}
                >
                  {deleteLoading ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Share row ──────────────────────────────────────── */}
        <div style={S.shareCard}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#475569',
                margin: '0 0 3px',
              }}
            >
              📎 Share this dashboard
            </p>
            <p
              style={{
                fontSize: 11,
                color: '#94a3b8',
                margin: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {window.location.href}
            </p>
          </div>
          <button
            className="copy-btn"
            onClick={handleCopyLink}
            style={{
              ...S.copyBtn,
              background: copied
                ? 'linear-gradient(135deg,#059669,#10B981)'
                : 'linear-gradient(135deg,#3B82F6,#7C3AED)',
            }}
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>

        {/* ── Admin action bar / read-only banner ───────────────── */}
        {isAdmin ? (
          <div>
            <button
              onClick={() => setShowAddPanel(!showAddPanel)}
              style={{
                width: '100%', padding: '13px', borderRadius: 14, border: 'none',
                background: showAddPanel
                  ? '#f1f5f9'
                  : 'linear-gradient(135deg,#3B82F6,#7C3AED)',
                color: showAddPanel ? '#475569' : '#fff',
                fontSize: 14, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: showAddPanel ? 'none' : '0 4px 14px rgba(109,40,217,.3)',
                transition: 'all .2s',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              <span style={{ fontSize: 18 }}>{showAddPanel ? '✕' : '+'}</span>
              {showAddPanel ? 'Cancel' : 'Add Expense'}
            </button>

            {showAddPanel && (
              <div style={{
                marginTop: 10, background: '#fff', borderRadius: 16, padding: 18,
                border: '1.5px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,.06)',
                display: 'flex', flexDirection: 'column', gap: 12,
              }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', margin: 0 }}>
                  New Expense
                </p>
                {addSuccess && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '8px 12px', fontSize: 13, color: '#166534' }}>
                    ✅ Expense added!
                  </div>
                )}
                <input
                  style={adminInput}
                  placeholder="Expense name"
                  value={newTxName}
                  onChange={e => setNewTxName(e.target.value)}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <input
                    style={adminInput}
                    type="number"
                    placeholder="Amount"
                    value={newTxAmount}
                    onChange={e => setNewTxAmount(e.target.value)}
                  />
                  <select
                    style={adminInput}
                    value={newTxCat}
                    onChange={e => setNewTxCat(e.target.value)}
                  >
                    {Object.entries(EXPENSE_CATEGORIES).map(([id, names]) => (
                      <option key={id} value={id}>{names.en}</option>
                    ))}
                  </select>
                </div>
                <input
                  style={adminInput}
                  placeholder="Note (optional)"
                  value={newTxNote}
                  onChange={e => setNewTxNote(e.target.value)}
                />
                <button
                  onClick={handleAddExpense}
                  disabled={addLoading}
                  style={{
                    padding: '11px', borderRadius: 10, border: 'none',
                    background: 'linear-gradient(135deg,#3B82F6,#7C3AED)',
                    color: '#fff', fontSize: 14, fontWeight: 600,
                    cursor: addLoading ? 'not-allowed' : 'pointer',
                    opacity: addLoading ? 0.7 : 1,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {addLoading ? 'Adding…' : 'Add Expense'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div style={S.readonlyBar}>
            <span style={{ fontSize: 14 }}>🔒</span>
            <span style={{ fontSize: 12, color: '#3B82F6', fontWeight: 500 }}>
              Read-only · Only the trip admin can add or edit expenses
            </span>
          </div>
        )}

        {/* ── Footer ────────────────────────────────────────── */}
        <p style={S.footer}>
          Powered by <strong style={{ color: '#7C3AED' }}>MyBalance</strong> ·
          Unova
        </p>
      </div>
    </div>
  );
}

// ── Admin input / button helpers ──────────────────────────────────────────
const adminInput: React.CSSProperties = {
  width: '100%', padding: '10px 12px', borderRadius: 10,
  border: '1.5px solid #e2e8f0', background: '#f8fafc',
  fontSize: 14, color: '#0f172a', outline: 'none', boxSizing: 'border-box',
  fontFamily: "'Plus Jakarta Sans', sans-serif",
};

const adminMiniBtn: React.CSSProperties = {
  padding: '3px 7px', borderRadius: 7, border: '1.5px solid #e2e8f0',
  background: '#F1F5FF', cursor: 'pointer', fontSize: 13, lineHeight: 1.4,
};

// ── Styles ────────────────────────────────────────────────────────────────
const S: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background:
      'linear-gradient(135deg, #EEF2FF 0%, #F0FDF4 50%, #FDF4FF 100%)',
    padding: '16px 16px 40px',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  container: {
    maxWidth: 480,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  center: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  loadRing: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    border: '3px solid #e2e8f0',
    borderTopColor: '#7C3AED',
    animation: 'spin .8s linear infinite',
  },

  // Hero
  heroCard: {
    background:
      'linear-gradient(135deg, #3B82F6 0%, #6D28D9 60%, #7C3AED 100%)',
    borderRadius: 20,
    padding: '20px 20px 16px',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 20px 40px rgba(109,40,217,.25), 0 4px 12px rgba(0,0,0,.1)',
  },
  heroBg1: {
    position: 'absolute',
    top: -30,
    right: -30,
    width: 120,
    height: 120,
    borderRadius: '50%',
    background: 'rgba(255,255,255,.06)',
  },
  heroBg2: {
    position: 'absolute',
    bottom: -20,
    left: -20,
    width: 90,
    height: 90,
    borderRadius: '50%',
    background: 'rgba(255,255,255,.04)',
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    position: 'relative',
  },
  brandIcon: {
    width: 30,
    height: 30,
    borderRadius: 8,
    background: 'rgba(255,255,255,.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
  },
  brandName: {
    fontSize: 13,
    fontWeight: 700,
    color: 'rgba(255,255,255,.9)',
    flex: 1,
    letterSpacing: '0.01em',
  },
  liveBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 11,
    fontWeight: 600,
    color: '#fff',
    background: 'rgba(16,185,129,.35)',
    padding: '3px 10px',
    borderRadius: 99,
    backdropFilter: 'blur(4px)',
    border: '1px solid rgba(16,185,129,.5)',
  },
  liveDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#10B981',
    display: 'inline-block',
    animation: 'livePulse 2s ease infinite',
  },
  heroTitle: {
    fontSize: 22,
    fontWeight: 800,
    color: '#fff',
    margin: '12px 0 4px',
    letterSpacing: '-0.01em',
    lineHeight: 1.2,
  },
  heroSub: {
    fontSize: 12,
    color: 'rgba(255,255,255,.7)',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  daysTag: {
    background: 'rgba(255,255,255,.15)',
    borderRadius: 99,
    padding: '2px 8px',
    fontSize: 11,
    color: '#fff',
    fontWeight: 600,
  },
  heroStats: {
    display: 'flex',
    marginTop: 16,
    background: 'rgba(0,0,0,.15)',
    borderRadius: 12,
    padding: '12px 16px',
    gap: 0,
    backdropFilter: 'blur(4px)',
    border: '1px solid rgba(255,255,255,.1)',
  },
  heroStat: { flex: 1, textAlign: 'center' },
  heroStatLabel: {
    fontSize: 10,
    color: 'rgba(255,255,255,.6)',
    marginBottom: 3,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  heroStatVal: { fontSize: 15, fontWeight: 700, color: '#fff' },
  heroStatDivider: {
    width: 1,
    background: 'rgba(255,255,255,.15)',
    margin: '0 4px',
  },
  heroBudgetBar: {
    marginTop: 14,
    height: 6,
    borderRadius: 99,
    background: 'rgba(255,255,255,.2)',
    overflow: 'hidden',
  },
  heroBudgetFill: {
    height: 6,
    borderRadius: 99,
    transition: 'width 1s cubic-bezier(0.34,1.56,0.64,1)',
  },
  heroBudgetLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 4,
  },

  // Insight cards
  insightRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3,1fr)',
    gap: 10,
  },
  insightCard: {
    background: '#fff',
    borderRadius: 14,
    padding: '12px 10px',
    border: '1px solid #e2e8f0',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,.04)',
    animation: 'fadeIn .4s ease',
  },
  insightIcon: { fontSize: 22, marginBottom: 4 },
  insightVal: { fontSize: 15, fontWeight: 700, color: '#0f172a' },
  insightLabel: {
    fontSize: 10,
    color: '#94a3b8',
    marginTop: 2,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },

  // Tabs
  tabBar: {
    display: 'flex',
    gap: 6,
    background: '#fff',
    borderRadius: 14,
    padding: 5,
    border: '1px solid #e2e8f0',
    boxShadow: '0 2px 8px rgba(0,0,0,.04)',
  },
  tabBtn: {
    flex: 1,
    padding: '9px 12px',
    borderRadius: 10,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    color: '#64748b',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  tabBtnActive: {
    background: 'linear-gradient(135deg, #3B82F6, #7C3AED)',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(109,40,217,.3)',
  },

  // Cards
  card: {
    background: '#fff',
    borderRadius: 16,
    padding: '16px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 2px 8px rgba(0,0,0,.04)',
  },
  emptyCard: {
    background: '#fff',
    borderRadius: 16,
    padding: '40px 20px',
    border: '1px solid #e2e8f0',
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    margin: '0 0 14px',
  },

  // Category chips
  catChips: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 8,
  },
  catChip: {
    background: '#fff',
    borderRadius: 12,
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    boxShadow: '0 1px 4px rgba(0,0,0,.04)',
  },

  // Transaction cards
  txCard: {
    background: '#fff',
    borderRadius: 14,
    padding: '12px 14px',
    border: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    boxShadow: '0 1px 4px rgba(0,0,0,.04)',
    opacity: 0,
    animationFillMode: 'forwards',
  },
  txIcon: {
    width: 42,
    height: 42,
    borderRadius: 12,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  txName: { fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 4 },
  txMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 11,
    color: '#64748b',
    flexWrap: 'wrap',
  },
  txCatTag: {
    borderRadius: 6,
    padding: '1px 6px',
    fontWeight: 600,
    fontSize: 10,
  },
  txNote: {
    fontSize: 11,
    color: '#94a3b8',
    marginTop: 4,
    background: '#f8fafc',
    borderRadius: 6,
    padding: '3px 7px',
    display: 'inline-block',
  },
  txAmt: {
    fontSize: 14,
    fontWeight: 700,
    color: '#EF4444',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },

  // Share
  shareCard: {
    background: '#fff',
    borderRadius: 14,
    padding: '12px 14px',
    border: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,.04)',
  },
  copyBtn: {
    padding: '8px 16px',
    borderRadius: 10,
    border: 'none',
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'all .2s',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },

  // Bottom
  readonlyBar: {
    background: 'linear-gradient(135deg, #EFF6FF, #F5F3FF)',
    borderRadius: 12,
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    border: '1px solid #BFDBFE',
  },
  footer: {
    textAlign: 'center',
    fontSize: 11,
    color: '#94a3b8',
    margin: '4px 0 0',
  },
};
